import {
  DataTable,
  DataTableColumn,
  DataTableOptions,
  DataType,
  FilterType,
  FormDialogMode,
  SelectOption,
  SortDirection,
  useNotify,
  UserPermission
} from '@globaleris/ui.library'
import { makeStyles, Menu, MenuItem, Typography, useTheme } from '@material-ui/core'
import {
  Autorenew,
  HighlightOff,
  InsertDriveFile,
  Pageview,
  PriorityHigh,
  Reply
} from '@material-ui/icons'
import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { styles } from './proceduresTable.styles'

import { EnumCommodity } from '../../../api'
import { DocumentTypes } from '../../../api/Argentia/enums/documentTypes'
import { enumKeyToValue, enumToSelectOptionArray, enumValueToKey } from '../../../helpers'
import { useConfirmationDialog, useHttpError, usePageState } from '../../../hooks'
import { GlobalNotification, NonAuthRoutes } from '../../../model'
import { ConfirmationDialogs } from '../../../model/enums/confirmationDialogs'

import {
  ProcedureDetailModel,
  ProcedureModel
} from '../../../pages/comunicationCenter/tabs/procedures/comunicationProcedures.model'
import {
  closeProcedure,
  createProcedure,
  fetchByCodOrigen,
  saveDocumentProcedure
} from '../../../pages/comunicationCenter/tabs/procedures/comunicationProdecures.service'
import { checkContractSignDate } from '../../../pages/contracts/contracts.service'
import { getCustomerDomain, storeDataTableRowsPerPageForCurrentPage } from '../../../redux'
import { DataTableErrorHandler } from '../../errors/scopes/dataTable/dataTableError'

import Close from '@material-ui/icons/Close'
import { useHistory } from 'react-router-dom'
import { Procedure, ProcedureDocument } from '../../../api/Argentia/endpoints/procedures'
import { AnnexedParameters, ProcedureStates, ProcedureTypes } from '../../../api/Argentia/enums'
import { EnumDocumentsNavigationFrom } from '../../../pages/documents/documents.model'
import { fetchAnnexedParams } from '../../../pages/procedures/procedures.service'
import { ProcedureFormMode } from '../../../pages/procedures/procedures.types'
import { AnnexedParamCustomFormDialog } from '../annexedParamCustomFormDialog/annexedParamCustomFormDialog'
import {
  FormStateConfig,
  InputFieldConfig
} from '../genericCustomFormDialog/genericCustomFormDialog.model'
import { ProcedureChangeStateFormDialog } from '../procedureChangeStateDialog/ProcedureChangeStateDialog'
import { ProcedureDetailDialog } from '../procedureDetailDialog/procedureDetailDialog'
import { ClaimInformationFormDialog } from './components/claimInformationFormDialog/claimInformationFormDialog'
import { IncidentReplyFormDialog } from './components/incidentReplyFormDialog/incidentReplyFormDialog'
import {
  mapClientNameOptions,
  mapNewProcedure,
  mapNewProcedureDocument
} from './proceduresTable.mapper'
import { AnnexedParamFormMode } from './proceduresTable.model'
import { fetchProcedureItem } from './proceduresTable.service'

export interface ProceduresTableProps {
  title?: React.ReactNode | string
  procedures?: ProcedureModel[]
  procedure?: ProcedureDetailModel
  onNotificationChange: (notificacion: GlobalNotification | undefined) => void
  onSelectProcedure: (procedure: number) => void
  onRefreshProcedures?: () => void
  cardMode?: boolean
  className?: string
  enableClientLink?: boolean
}

const initialFormStateConfig: FormStateConfig = {
  formMode: FormDialogMode.ADD,
  open: false,
  inputFields: [] as InputFieldConfig[],
  title: ''
}

interface CancelMenuConfig {
  options: any[]
  anchorEl: (EventTarget & Element) | null
  open: boolean
}

const MAX_CHAR_COMMENTS = 450

export const ProceduresTable: React.FC<ProceduresTableProps> = ({
  procedures,
  procedure,
  onNotificationChange,
  onSelectProcedure,
  onRefreshProcedures,
  cardMode = false,
  title = '',
  className = '',
  enableClientLink = false
}) => {
  const [t] = useTranslation(['globalerisLibrary', 'comunicationProcedures', 'enums', 'procedures'])

  const currentTheme = useTheme()
  const useStyles = makeStyles(styles(currentTheme))
  const classes = useStyles()
  const { info, error } = useNotify()
  const history = useHistory()
  const [clientNameOptions, setClientNameOptions] = useState<SelectOption[]>(
    mapClientNameOptions([])
  )
  // Handler para navegar a la ficha del cliente
  const handleClientLinkClick = (row: ProcedureModel) => {
    if (row.clientData?.id) {
      history.push(NonAuthRoutes.ClientFile, {
        clientID: row.clientData.id,
        alternativeNavigationEnabled: true,
        fromCommunicationCenter: true
      })
    }
  }

  const { showError, clearError, errorProps } = useHttpError()
  const { getConfirmation, closeConfirmation } = useConfirmationDialog()
  const { dataTableRowsPerPage } = usePageState()
  const [showFormDetail, setShowFormDetail] = useState<boolean>(false)
  const [annexedParamFormStateConfig, setAnnexedParamFormStateConfig] =
    useState<FormStateConfig>(initialFormStateConfig)
  const [annexedParamFormMode, setAnnexedParamFormMode] = useState<AnnexedParamFormMode>(
    AnnexedParamFormMode.NULL
  )
  const [showChangeStateDialog, setShowChangeStateDialog] = useState<boolean>(false)
  const [procedureItemToChangeState, setProcedureItemToChangeState] =
    useState<ProcedureModel | null>(null)

  const [cancelMenu, setCancelMenu] = React.useState<CancelMenuConfig>({
    open: false,
    options: [],
    anchorEl: null
  })

  const pendingInfoCache = useRef<Map<number, any>>(new Map())
  const changeStateCache = useRef<Map<number, any>>(new Map())

  const maxChar = MAX_CHAR_COMMENTS

  const [incidentReplyList, setIncidentReplyList] = useState<SelectOption[]>([])
  const [infoTypeList, setInfoTypeList] = useState<SelectOption[]>([])
  const [isGasNatural, setIsGasNatural] = useState<boolean>(false)
  const [claimDialogOpen, setClaimDialogOpen] = useState(false)
  const [formDialogOpen, setFormDialogOpen] = useState<boolean>(false)
  const [formMode, setFormMode] = useState<FormDialogMode>(FormDialogMode.ADD)
  const [formInputData, setFormInputData] = useState<any>({})
  const [selectedProcedureRowItem, setSelectedProcedureRowItem] = useState<ProcedureModel | null>(
    null
  )

  // ---------------------------- Functions --------------------------- //
  // #region

  const getDocumentType = (procedureItem: Procedure, docType: DocumentTypes) => {
    return (
      procedureItem.documentsList?.filter(
        (doc: ProcedureDocument) => enumKeyToValue(DocumentTypes, doc.type) === docType
      ) || []
    )
  }

  const mapAnnexedParamValues = (paramId: number, responseData: any) => {
    return responseData
      .filter(param => param.id === paramId)[0]
      .annexedParamsValues.map(param => ({
        paramId: param.id,
        value: param.code,
        label: param.description
      }))
  }

  // #endregion
  // ---------------------------- Functions --------------------------- //
  const onCloseActionHandler = async (item: ProcedureModel) => {
    getConfirmation(ConfirmationDialogs.CLOSE_PROCEDURE).then(async () => {
      await closeProcedure(item).then(async response => {
        if (response.success) {
          info(t('comunicationProcedures:notifications.success.closeMessage'))
          onRefreshProcedures && onRefreshProcedures()
        } else {
          error(t('comunicationProcedures:notifications.error.closeError'))
        }
        closeConfirmation()
      })
    })
  }

  const onDocumentAddHandler = async (row: ProcedureModel) => {
    history.push(NonAuthRoutes.Documents, {
      elementId: row.id,
      elementCode: row.code,
      commodity: row.cupsModel?.commodity,
      navigation: EnumDocumentsNavigationFrom.Procedures
    })
  }

  // ------------------ Annexed Param Form Dialog Handlers ------------------ //
  //#region
  const onCancelActionHandler = async (item: ProcedureModel) => {
    // Define Input Fields for Form Dialog Component
    let inputFields: InputFieldConfig[] = [
      {
        name: 'comments',
        label: t('procedures:components.annexedParamCustomFormDialog.inputs.comments'),
        type: DataType.LONGTEXT,
        maxLength: maxChar
      }
    ]
    // Set Form Dialog Component State
    setAnnexedParamFormStateConfig({
      formMode: FormDialogMode.ADD,
      open: true,
      inputFields: inputFields,
      title: t('procedures:components.annexedParamCustomFormDialog.title.cancellation'),
      subtitle: t('procedures:components.annexedParamCustomFormDialog.subtitle.cancellation'),
      item
    })
    setAnnexedParamFormMode(AnnexedParamFormMode.CANCELLATION)
  }

  const onWithdrawalActionHandler = async (item: ProcedureModel) => {
    // define withdraw Function
    const openWithdrawalForm = async () => {
      // Fetch Annexed Params
      let withdrawalTypeList: SelectOption[] = []
      const request = [AnnexedParameters.TIPO_DESISTIMIENTO]
      const response = await fetchAnnexedParams(request)
      if (response.success && response.data) {
        withdrawalTypeList = mapAnnexedParamValues(request[0], response.data)
      }

      // Define Input Fields for Form Dialog Component
      const inputFields: InputFieldConfig[] = [
        {
          name: 'withdrawalType',
          label: t('procedures:components.annexedParamCustomFormDialog.inputs.withdrawalType'),
          type: DataType.SELECT,
          selectOptions: withdrawalTypeList,
          required: true
        },
        {
          name: 'comments',
          label: t('procedures:components.annexedParamCustomFormDialog.inputs.comments'),
          type: DataType.TEXT
        },
        {
          name: 'originProcedureId',
          display: false,
          label: 'no-label',
          type: DataType.NUMBER,
          defaultValue: item.id
        }
      ]

      // Set Form Dialog Component State
      setAnnexedParamFormStateConfig({
        formMode: FormDialogMode.ADD,
        open: true,
        inputFields: inputFields,
        title: t('procedures:components.annexedParamCustomFormDialog.title.withdrawal'),
        item
      })
      setAnnexedParamFormMode(AnnexedParamFormMode.WITHDRAWAL)
    }

    // validate contract sign date
    const contractResponse = await checkContractSignDate(item.id)
    const isInWithdrawalPeriod = contractResponse?.data
    if (!isInWithdrawalPeriod) {
      getConfirmation(ConfirmationDialogs.WITHDRAWAL_PROCEDURE).then(async () => {
        await openWithdrawalForm()
        closeConfirmation()
      })
    } else {
      await openWithdrawalForm()
    }
  }

  const onReinstatementActionHandler = async (item: ProcedureModel) => {
    // Fetch Annexed Params
    let reinstatementTypeList: SelectOption[] = []
    const request = [AnnexedParameters.TIPO_REPOSICION]
    const response = await fetchAnnexedParams(request)
    const isSaliente =
      item.type === ProcedureTypes.ClienteSaliente_PWR ||
      item.type === ProcedureTypes.ClienteSaliente

    if (response.success && response.data) {
      reinstatementTypeList = mapAnnexedParamValues(request[0], response.data)

      if (!isSaliente) {
        reinstatementTypeList = reinstatementTypeList.filter(item => item.value !== '03')
      }
    }

    // Define Input Fields for Form Dialog Component
    let inputFields: InputFieldConfig[] = [
      {
        name: 'reinstatementType',
        label: t('procedures:components.annexedParamCustomFormDialog.inputs.reinstatementType'),
        type: DataType.SELECT,
        selectOptions: reinstatementTypeList,
        readOnly: isSaliente,
        defaultValue: isSaliente ? '03' : undefined,
        required: true
      },
      {
        name: 'comments',
        label: t('procedures:components.annexedParamCustomFormDialog.inputs.comments'),
        type: DataType.TEXT,
        display: item.cupsModel?.commodity === EnumCommodity.GasNatural
      },
      {
        name: 'originProcedureId',
        display: false,
        label: 'no-label',
        type: DataType.NUMBER,
        defaultValue: item.id
      }
    ]
    // Set Form Dialog Component State
    setAnnexedParamFormStateConfig({
      formMode: FormDialogMode.ADD,
      open: true,
      inputFields: inputFields,
      title: t('procedures:components.annexedParamCustomFormDialog.title.reinstatement'),
      item
    })
    setAnnexedParamFormMode(AnnexedParamFormMode.REINSTATEMENT)
  }

  const onReinstatementReplyActionHandler = async (item: ProcedureModel) => {
    // const procedureItem: Procedure = (await fetchProcedureItem(item?.id)).data
    // const paso_15 = getDocumentType(procedureItem, DocumentTypes.Paso_15)

    // if (paso_15 && paso_15.length > 0) {
    // Fetch Annexed Params
    let rejectionReasonList: SelectOption[] = []
    const request =
      item.type === ProcedureTypes.Reposicion
        ? [AnnexedParameters.MOTIVO_RECHAZO_REPOSICION]
        : [AnnexedParameters.GAS_MOTIVO_RECHAZO_REPOSICION]
    const response = await fetchAnnexedParams(request)
    if (response.success && response.data) {
      rejectionReasonList = mapAnnexedParamValues(request[0], response.data)
    }

    const handleReinstatementReplyTypeChange = (value: string | number, _formState: object) => {
      const selected = value

      const updatedFields: InputFieldConfig[] = [
        {
          name: 'reinstatementReplySelect',
          label: t(
            'procedures:components.annexedParamCustomFormDialog.title.reinstatementReplySelect'
          ),
          type: DataType.SELECT,
          selectOptions: [
            {
              label: t('procedures:components.annexedParamCustomFormDialog.buttons.accept'),
              value: 'ACCEPT'
            },
            {
              label: t('procedures:components.annexedParamCustomFormDialog.buttons.reject'),
              value: 'REJECT'
            }
          ],
          defaultValue: selected,
          onChange: handleReinstatementReplyTypeChange
        }
      ]

      if (selected === 'REJECT') {
        updatedFields.push(
          {
            name: 'rejectionReason',
            label: t(
              'procedures:components.annexedParamCustomFormDialog.inputs.reinstatementRejectionReason'
            ),
            type: DataType.SELECT,
            required: true,
            selectOptions: rejectionReasonList
          },
          {
            name: 'comments',
            label: t('procedures:components.annexedParamCustomFormDialog.inputs.comments'),
            type: DataType.LONGTEXT,
            required: item.type === ProcedureTypes.Reposicion,
            maxLength: 400
          }
        )
      }

      setAnnexedParamFormStateConfig(prev => ({
        ...prev,
        inputFields: updatedFields
      }))
    }

    // Define Input Fields for Form Dialog Component
    let inputFields: InputFieldConfig[] = [
      {
        name: 'reinstatementReplySelect',
        label: t(
          'procedures:components.annexedParamCustomFormDialog.title.reinstatementReplySelect'
        ),
        type: DataType.SELECT,
        selectOptions: [
          {
            label: t('procedures:components.annexedParamCustomFormDialog.buttons.accept'),
            value: 'ACCEPT'
          },
          {
            label: t('procedures:components.annexedParamCustomFormDialog.buttons.reject'),
            value: 'REJECT'
          }
        ],
        onChange: handleReinstatementReplyTypeChange
      }
    ]

    // Set Form Dialog Component State
    setAnnexedParamFormStateConfig({
      formMode: FormDialogMode.ADD,
      open: true,
      inputFields: inputFields,
      title: t('procedures:components.annexedParamCustomFormDialog.title.reinstatementReply'),
      item
    })
    setAnnexedParamFormMode(AnnexedParamFormMode.SEND_REINSTATEMENT_REPLY)
    // } else {
    //   error(
    //     t(
    //       'procedures:components.annexedParamCustomFormDialog.notifications.error.reinstatementReply'
    //     )
    //   )
    // }
  }

  const onPendingInformationActionHandler = async (item: ProcedureModel) => {
    let updatedItem = item
    const isGas = item.cupsModel?.commodity === EnumCommodity.GasNatural

    // For Suspension procedures, check if there is a Reenganche in "PendienteInformacion" state.
    // If so, use its id in the form to create the related "tramitacionDocumento".
    if (item.type === ProcedureTypes.CorteSuministroGas) {
      const reconnectionProcedure = await getReconnectionProcedure(item, pendingInfoCache.current)

      if (
        reconnectionProcedure !== null &&
        enumValueToKey(ProcedureStates, reconnectionProcedure.state) ===
          ProcedureStates.PendienteInformacion
      ) {
        updatedItem = { ...item, id: reconnectionProcedure.id }
      }
    }

    // Fetch Annexed Params
    const annexParam = isGas
      ? AnnexedParameters.GAS_CONTESTACION_INCIDENCIA
      : AnnexedParameters.PWR_CONTESTACION_INCIDENCIA

    const response = await fetchAnnexedParams([annexParam])
    if (response.success && response.data) {
      setIncidentReplyList(mapAnnexedParamValues(annexParam, response.data))
    }

    setIsGasNatural(isGas)
    setSelectedProcedureRowItem(updatedItem)
    setAnnexedParamFormMode(AnnexedParamFormMode.INCIDENT_REPLY)
    setFormMode(FormDialogMode.ADD)
    setFormDialogOpen(true)
  }

  const onChangeStateActionHandler = async (item: ProcedureModel) => {
    let updatedItem = item

    // For Suspension procedures, check if there is a Reenganche in "Anulacion_solicitada" state.
    // If so, use its id in the form to create the related "tramitacionDocumento".
    if (item.type === ProcedureTypes.CorteSuministroGas) {
      const reconnectionProcedure = await getReconnectionProcedure(item, changeStateCache.current)

      if (
        reconnectionProcedure !== null &&
        enumKeyToValue(ProcedureStates, reconnectionProcedure.state) ===
          ProcedureStates.Anulacion_solicitada
      ) {
        updatedItem = { ...item, id: reconnectionProcedure.id }
      }
    }

    setProcedureItemToChangeState(updatedItem)
    setShowChangeStateDialog(true)
  }
  const onCloseChangeStateActionHandler = async () => {
    setShowChangeStateDialog(false)
    closeConfirmation()
  }

  const getReconnectionProcedure = async (
    item: ProcedureModel,
    cache: Map<number, any>
  ): Promise<Procedure | null> => {
    const cached = cache.get(item.id)

    if (cached || cached === null) return cached

    const response = await fetchByCodOrigen(item.cupsModel?.id, item.code)
    const procedure: Procedure = response.data?.[0] ?? null

    cache.set(item.id, procedure)

    return procedure
  }

  const onPendingInformationClaimActionHandler = async (item: ProcedureModel) => {
    setIsGasNatural(item.cupsModel?.commodity === EnumCommodity.GasNatural)

    const annexedParamsRequest = isGasNatural
      ? [AnnexedParameters.GAS_TIPO_INFORMACION_ADICIONAL]
      : [AnnexedParameters.PWR_TIPO_INFORMACION_ADICIONAL]
    const annexedParamsResponse = await fetchAnnexedParams(annexedParamsRequest)
    if (annexedParamsResponse.success && annexedParamsResponse.data) {
      setInfoTypeList(mapAnnexedParamValues(annexedParamsRequest[0], annexedParamsResponse.data))
    }

    const procedureItem: Procedure = (await fetchProcedureItem(item?.id)).data

    setSelectedProcedureRowItem(procedureItem)
    setAnnexedParamFormMode(AnnexedParamFormMode.SEND_CLAIM_INFORMATION)
    setFormMode(FormDialogMode.ADD)
    setClaimDialogOpen(true)
  }

  const handleAnnexedParamFormDialogClose = async (formData: any) => {
    if (formData) {
      switch (annexedParamFormMode) {
        case AnnexedParamFormMode.REINSTATEMENT:
          saveProcedure(
            annexedParamFormMode,
            formData,
            annexedParamFormStateConfig.item.cupsModel?.commodity,
            annexedParamFormStateConfig.item
          )
          break
        case AnnexedParamFormMode.SEND_REINSTATEMENT_REPLY:
          saveProcedureDocument(
            annexedParamFormMode,
            formData,
            annexedParamFormStateConfig.item.cupsModel?.commodity,
            annexedParamFormStateConfig.item
          )
          break
        case AnnexedParamFormMode.WITHDRAWAL:
          saveProcedure(
            annexedParamFormMode,
            formData,
            annexedParamFormStateConfig.item.cupsModel?.commodity,
            annexedParamFormStateConfig.item
          )
          break
        case AnnexedParamFormMode.INCIDENT_REPLY:
          saveProcedureDocument(
            annexedParamFormMode,
            formData,
            selectedProcedureRowItem?.cupsModel?.commodity!,
            selectedProcedureRowItem!
          )
          break
        case AnnexedParamFormMode.SEND_CLAIM_INFORMATION:
          saveProcedureDocument(
            annexedParamFormMode,
            formData,
            selectedProcedureRowItem?.cupsModel?.commodity!,
            selectedProcedureRowItem!
          )
          break
        case AnnexedParamFormMode.CANCELLATION:
          annexedParamFormStateConfig.item.cupsModel?.commodity === EnumCommodity.GasNatural
            ? saveProcedure(
                annexedParamFormMode,
                formData,
                annexedParamFormStateConfig.item.cupsModel?.commodity,
                annexedParamFormStateConfig.item
              )
            : saveProcedureDocument(
                annexedParamFormMode,
                formData,
                annexedParamFormStateConfig.item.cupsModel?.commodity,
                annexedParamFormStateConfig.item
              )
          break
        default:
          break
      }
    }
    setAnnexedParamFormStateConfig(initialFormStateConfig)
  }
  //#endregion
  //------------------ Annexed Param Form Dialog Handlers ------------------ //

  // ------------------ Annexed Param Form Dialog Functions ------------------ //
  //#region
  const saveProcedure = async (
    annexedParamFormMode: AnnexedParamFormMode,
    formData: any,
    commodity: EnumCommodity,
    item: ProcedureModel
  ) => {
    const request = mapNewProcedure(annexedParamFormMode, formData, commodity, item)

    await createProcedure(request).then(response => {
      if (response.success) {
        info(
          t(
            'procedures:components.annexedParamCustomFormDialog.notifications.success.createProcedure'
          )
        )
        onRefreshProcedures && onRefreshProcedures()
      } else {
        error(
          t(
            'procedures:components.annexedParamCustomFormDialog.notifications.error.createProcedure'
          )
        )
      }
    })
  }

  const saveProcedureDocument = async (
    annexedParamFormMode: AnnexedParamFormMode,
    formData: any,
    commodity: EnumCommodity,
    item: ProcedureModel
  ) => {
    const request = mapNewProcedureDocument(annexedParamFormMode, formData, commodity, item)

    await saveDocumentProcedure(request).then(response => {
      if (response.success) {
        info(
          t(
            'procedures:components.annexedParamCustomFormDialog.notifications.success.createProcedure'
          )
        )
        onRefreshProcedures && onRefreshProcedures()
      } else {
        error(
          t(
            'procedures:components.annexedParamCustomFormDialog.notifications.error.createProcedure'
          )
        )
      }
    })
  }

  //#endregion
  //------------------ Annexed Param Form Dialog Functions ------------------ //

  // --------------------- Anchored Cancel Menu Config --------------------- //
  // #region
  const onCancelMenuActionHandler = async (item: ProcedureModel, event: React.MouseEvent) => {
    if (item.type === ProcedureTypes.CorteSuministroGas) {
      history.push(NonAuthRoutes.ProceduresAdd, {
        procedureId: item.id,
        cupsCode: item.cups,
        formMode: ProcedureFormMode.RENGANCHE_CONTRATO_GAS
      })

      return
    }

    if (
      item.type === ProcedureTypes.Baja_Suministro_GAS ||
      item.type === ProcedureTypes.CorteSuministroPWR
    ) {
      onCancelActionHandler(item)
      return
    }

    let optionsButton: any = []

    item.canCancel &&
      optionsButton.push({
        label: t('procedures:components.genericDataTable.tooltips.cancel'),
        onClick: () => onCancelActionHandler(item)
      })
    item.canWithdraw &&
      optionsButton.push({
        label: t('procedures:components.genericDataTable.tooltips.withdrawal'),
        onClick: () => onWithdrawalActionHandler(item)
      })
    item.canReinstatement &&
      optionsButton.push({
        label: t('procedures:components.genericDataTable.tooltips.reinstatement'),
        onClick: () => onReinstatementActionHandler(item)
      })

    setCancelMenu(prevState => ({
      ...prevState,
      options: optionsButton,
      anchorEl: event.currentTarget
    }))
  }

  const onCancelMenuCloseHandler = () => {
    setCancelMenu({
      open: false,
      options: [],
      anchorEl: null
    })
  }

  useEffect(() => {
    cancelMenu.options.length > 0 &&
      setCancelMenu(prevState => ({
        ...prevState,
        open: true
      }))
  }, [cancelMenu.options])

  // #endregion
  // --------------------- Anchored Cancel Menu Config --------------------- //

  const dataTableColumns: DataTableColumn[] = [
    {
      name: 'id',
      display: false
    },
    {
      name: 'actions',
      label: '',
      hideForResponsive: true,
      type: DataType.HTML,
      columnWidth: 3
    },
    {
      name: 'date',
      label: t('comunicationProcedures:dataTable.columns.procedureDate'),
      type: DataType.DATE
    },
    {
      name: 'type',
      label: t('comunicationProcedures:dataTable.columns.procedureType'),
      type: DataType.SELECT,
      selectOptions: enumToSelectOptionArray(ProcedureTypes, 'enums:ProcedureTypes')
    },
    {
      name: 'state',
      label: t('comunicationProcedures:dataTable.columns.state'),
      type: DataType.SELECT,
      selectOptions: enumToSelectOptionArray(ProcedureStates, 'enums:ProcedureStates')
    },
    {
      name: 'cups',
      label: t('comunicationProcedures:dataTable.columns.cups'),
      type: DataType.TEXT
    },
    {
      name: 'code',
      label: t('comunicationProcedures:dataTable.columns.code'),
      type: DataType.TEXT
    },
    {
      name: 'client',
      label: t('comunicationProcedures:dataTable.columns.client'),
      type: DataType.TEXT,
      display: !enableClientLink
    },
    {
      name: 'clientData.id',
      label: t('comunicationProcedures:dataTable.columns.client'),
      type: DataType.LINK,
      selectOptions: clientNameOptions,
      onLinkClick: selectedRow => {
        if (enableClientLink && selectedRow.clientData?.id) {
          handleClientLinkClick(selectedRow)
        }
      },
      display: enableClientLink
    }
  ]

  const dataTableOptions: DataTableOptions = {
    i18n: t('globalerisLibrary:dataTable', { returnObjects: true }),
    search: true,
    elevation: 0,
    filterType: FilterType.ADVANCED,
    download: !cardMode,
    canEdit: false,
    canDelete: false,
    canInsert: false,
    selectableRows: false,
    customInsertEditModal: true,
    rowsPerPage: dataTableRowsPerPage || 5,
    rowsPerPageOptions: [5, 10, 20],
    sortOrder: { name: 'date', direction: SortDirection.DESC },
    analytics: {
      customer: getCustomerDomain(),
      view: t('comunicationProcedures:dataTable.analytics')
    },
    rowCustomActions: [
      {
        icon: <Pageview fontSize="small" />,
        tooltip: t('comunicationProcedures:dataTable.tooltips.view'),
        onClick: async (row: ProcedureModel) => {
          onSelectProcedure(row.id)
          setShowFormDetail(true)
        }
      },
      {
        userPermission: UserPermission.EDIT,
        icon: <HighlightOff fontSize="small" />,
        tooltip: t('comunicationProcedures:dataTable.customActions.cancelOrWithdraw'),
        visibleIf: 'canCancelOrWithdraw',
        onClick: onCancelMenuActionHandler
      },
      {
        userPermission: UserPermission.EDIT,
        icon: <HighlightOff fontSize="small" />,
        tooltip: t('comunicationProcedures:dataTable.customActions.reconnect'),
        visibleIf: 'canReconnect',
        onClick: onCancelMenuActionHandler
      },
      {
        userPermission: UserPermission.EDIT,
        icon: <HighlightOff fontSize="small" />,
        tooltip: t('comunicationProcedures:dataTable.customActions.cancel'),
        visibleIf: 'canCancelBajaGas',
        onClick: onCancelMenuActionHandler
      },
      {
        userPermission: UserPermission.EDIT,
        icon: <Reply fontSize="small" />,
        tooltip: t('comunicationProcedures:dataTable.customActions.pendingInfo'),
        visibleIf: 'isPendingInformation',
        onClick: onPendingInformationActionHandler
      },
      {
        userPermission: UserPermission.EDIT,
        icon: <Reply fontSize="small" />,
        tooltip: t('comunicationProcedures:dataTable.customActions.sendInfo'),
        visibleIf: 'isPendingInformationClaim',
        onClick: onPendingInformationClaimActionHandler
      },
      {
        userPermission: UserPermission.EDIT,
        icon: <Reply fontSize="small" />,
        tooltip: t('comunicationProcedures:dataTable.customActions.sendReinstatementInfo'),
        visibleIf: 'isPendingInformationReinstatement',
        onClick: onReinstatementReplyActionHandler
      },
      {
        userPermission: UserPermission.EDIT,
        icon: <Autorenew fontSize="small" />,
        tooltip: t('comunicationProcedures:dataTable.customActions.changeState'),
        visibleIf: 'canChangeState',
        onClick: onChangeStateActionHandler
      },
      {
        icon: <PriorityHigh fontSize="small" />,
        tooltip: t('procedures:components.genericDataTable.tooltips.complaint'),
        onClick: async (row: ProcedureModel) => {
          history.push(NonAuthRoutes.ProceduresAdd, {
            procedureId: row.id,
            formMode: ProcedureFormMode.RECLAMACION_TRAMITACION,
            clientData: row.clientData
          })
        }
      },
      {
        icon: <InsertDriveFile fontSize="small" />,
        tooltip: t('procedures:components.genericDataTable.tooltips.documents'),
        onClick: onDocumentAddHandler
      },
      {
        icon: <Close fontSize="small" />,
        tooltip: t('comunicationProcedures:dataTable.customActions.close'),
        visibleIf: 'canClosed',
        onClick: onCloseActionHandler
      }
    ],
    onRowsPerPageChange: (rowsPerPage: number) => {
      storeDataTableRowsPerPageForCurrentPage(rowsPerPage)
    }
  }

  // --------------------- Form Dialog Close Handlers -----------------------------//
  const handleIncidentReplyFormDialogClose = (formData: any) => {
    if (formData) {
      if (formMode === FormDialogMode.ADD) {
        handleAnnexedParamFormDialogClose(formData)
      }
    }
    setFormDialogOpen(false)
    setClaimDialogOpen(false)
    setFormInputData({})
  }

  useEffect(() => {
    if (procedures) {
      setClientNameOptions(mapClientNameOptions(procedures))
    }
  }, [procedures])

  return (
    <>
      <DataTable
        className={className}
        title={cardMode ? title : undefined}
        data={procedures}
        columns={dataTableColumns}
        options={dataTableOptions}
        info={DataTableErrorHandler(errorProps)}
      />

      <ProcedureDetailDialog
        procedure={procedure}
        showFormDetail={showFormDetail}
        handleClose={() => setShowFormDetail(false)}
      />

      <AnnexedParamCustomFormDialog
        formStateConfig={{ ...annexedParamFormStateConfig }}
        onClose={handleAnnexedParamFormDialogClose}
        title={annexedParamFormStateConfig.title ?? ''}
        subtitle={annexedParamFormStateConfig.subtitle ?? ''}
      />

      <Menu
        keepMounted={false}
        elevation={2}
        anchorEl={cancelMenu.anchorEl}
        open={Boolean(cancelMenu.anchorEl)}
        onClose={onCancelMenuCloseHandler}
      >
        {cancelMenu.options.map((optionButton, index) => (
          <MenuItem
            dense
            key={index}
            onClick={() => {
              optionButton.onClick()
              onCancelMenuCloseHandler()
            }}
          >
            <Typography variant="inherit">{optionButton.label}</Typography>
          </MenuItem>
        ))}
      </Menu>
      <ProcedureChangeStateFormDialog
        procedureState={procedureItemToChangeState?.state!}
        procedureType={procedureItemToChangeState?.type!}
        procedureParameters={procedureItemToChangeState?.parameters!}
        commodity={procedureItemToChangeState?.cupsModel?.commodity!}
        procedureId={procedureItemToChangeState?.id}
        originProcedureId={procedureItemToChangeState?.originProcedureId}
        onClose={onCloseChangeStateActionHandler}
        open={showChangeStateDialog}
      />
      <IncidentReplyFormDialog
        open={formDialogOpen}
        onClose={handleIncidentReplyFormDialogClose}
        incidentReplyList={incidentReplyList}
        isGasNatural={isGasNatural}
      />

      <ClaimInformationFormDialog
        open={claimDialogOpen}
        onClose={handleIncidentReplyFormDialogClose}
        infoTypeList={infoTypeList}
        isGasNatural={isGasNatural}
        procedureDocument={selectedProcedureRowItem?.documentsList!}
      />
    </>
  )
}
